import { DomUtils } from "./DomUtils.js";

export class XPathWatchProcessor {
	constructor(vroc) {
		this.vroc = vroc;
		this.watchElements = new Map(); // xpath -> Set<elements>
		this.watchPatterns = new Map(); // xpath -> RegExp pattern
	}

	/**
	 * Регистрирует элемент с data-xpath-watch
	 * @param {HTMLElement} element - DOM элемент
	 * @param {string} xpath - XPath паттерн для отслеживания
	 */
	registerWatchElement(element, xpath) {
		if (!this.watchElements.has(xpath)) {
			this.watchElements.set(xpath, new Set());
			this.watchPatterns.set(xpath, this._createXPathPattern(xpath));
		}
		this.watchElements.get(xpath).add(element);
	}

	/**
	 * Отменяет регистрацию watch элемента
	 * @param {HTMLElement} element - DOM элемент
	 * @param {string} xpath - XPath паттерн
	 */
	unregisterWatchElement(element, xpath) {
		if (this.watchElements.has(xpath)) {
			this.watchElements.get(xpath).delete(element);
			if (this.watchElements.get(xpath).size === 0) {
				this.watchElements.delete(xpath);
				this.watchPatterns.delete(xpath);
			}
		}
	}

	/**
	 * Проверяет, затрагивает ли патч watch-элементы
	 * @param {Object} patch - Патч для проверки
	 * @returns {Array<string>} - Массив xpath паттернов, которые затрагивает патч
	 */
	getAffectedWatchPatterns(patch) {
		const affectedPatterns = [];

		for (const [xpath, pattern] of this.watchPatterns) {
			if (this._matchesXPathPattern(patch.xpath, xpath, pattern)) {
				affectedPatterns.push(xpath);
			}
		}

		return affectedPatterns;
	}

	/**
	 * Выполняет полную перерисовку для watch-элементов
	 * @param {string} id - ID компонента
	 * @param {Array<string>} affectedPatterns - Затронутые xpath паттерны
	 */
	async triggerFullRerender(id, affectedPatterns) {
		const component = this.vroc.components.get(id);
		if (!component) return;

		// Оптимизация: рендерим компонент только один раз для всех паттернов
		await this._rerenderAllWatchElements(id, affectedPatterns);
	}

	/**
	 * Оптимизированная перерисовка всех watch-элементов - рендерим компонент только один раз
	 * @param {string} id - ID компонента
	 * @param {Array<string>} affectedPatterns - Затронутые xpath паттерны
	 */
	async _rerenderAllWatchElements(id, affectedPatterns) {
		const component = this.vroc.components.get(id);
		if (!component) return;

		// Собираем все watch-элементы для всех затронутых паттернов
		const allWatchElements = [];
		for (const xpath of affectedPatterns) {
			const watchElements = this.watchElements.get(xpath);
			if (watchElements && watchElements.size > 0) {
				for (const element of watchElements) {
					// Проверяем, что элемент все еще присутствует в DOM
					if (document.body.contains(element)) {
						allWatchElements.push({ element, xpath });
					} else {
						// Элемент был удален из DOM, отменяем его регистрацию
						this.unregisterWatchElement(element, xpath);
					}
				}
			}
		}

		if (allWatchElements.length === 0) return;

		// Получаем актуальное XML состояние
		const currentXml = component.state.serialize();

		// Создаем временный контейнер для рендеринга - ТОЛЬКО ОДИН РАЗ!
		const tempContainer = document.createElement('div');
		
		// Рендерим весь компонент заново - ТОЛЬКО ОДИН РАЗ!
		await this.vroc.renderFragment(
			id,
			tempContainer,
			new DOMParser().parseFromString(currentXml, "text/xml"),
			null, // без патчей
			false // не заменяем контейнер
		);

		// Теперь обновляем все watch-элементы используя один отрендеренный результат
		for (const { element, xpath } of allWatchElements) {
			try {
				// Ищем соответствующий элемент в новом рендере
				const newContent = this._findCorrespondingElement(tempContainer, element, component.container);

				if (newContent) {
					// Заменяем старый элемент новым содержимым
					element.replaceWith(newContent.cloneNode(true));

					// Перерегистрируем watch-элементы после замены
					this._updateWatchElementsAfterRerender(id, newContent);

				} else {
					// Элемент не найден в новом рендере - значит он был удален из XML
					console.log(`[XPathWatchProcessor] Element with xpath ${xpath} was removed from XML, removing from DOM`);

					// Отменяем регистрацию
					this.unregisterWatchElement(element, xpath);

					// Удаляем из DOM
					element.remove();
				}
			} catch (error) {
				console.error(`[XPathWatchProcessor] Error rerendering element for xpath ${xpath}:`, error);
			}
		}
	}

	/**
	 * Перерисовывает все watch-элементы для конкретного xpath (старый неоптимизированный метод)
	 * @param {string} id - ID компонента
	 * @param {string} xpath - XPath паттерн
	 */
	async _rerenderWatchElements(id, xpath) {
		const component = this.vroc.components.get(id);
		const watchElements = this.watchElements.get(xpath);

		if (!watchElements || watchElements.size === 0) return;

		// Получаем актуальное XML состояние
		const currentXml = component.state.serialize();

		// Перерисовываем каждый watch-элемент асинхронно, без блокировки
		const rerenderPromises = Array.from(watchElements).map(element =>
			this._rerenderSingleElement(id, element, xpath, currentXml)
		);

		// Ждем завершения всех перерисовок
		await Promise.all(rerenderPromises);
	}

	/**
	 * Перерисовывает один watch-элемент
	 * @param {string} id - ID компонента
	 * @param {HTMLElement} element - DOM элемент
	 * @param {string} xpath - XPath паттерн
	 * @param {string} currentXml - Актуальное XML состояние
	 */
	async _rerenderSingleElement(id, element, xpath, currentXml) {
		try {
			const component = this.vroc.components.get(id);
			if (!component) return;

			// Создаем временный контейнер для рендеринга
			const tempContainer = document.createElement('div');

			// Рендерим весь компонент заново
			await this.vroc.renderFragment(
				id,
				tempContainer,
				new DOMParser().parseFromString(currentXml, "text/xml"),
				null, // без патчей
				false // не заменяем контейнер
			);

			// Ищем соответствующий элемент в новом рендере по тому же пути относительно контейнера
			const newContent = this._findCorrespondingElement(tempContainer, element, component.container);

			if (newContent) {
				// Заменяем старый элемент новым содержимым
				element.replaceWith(newContent.cloneNode(true));

				// Перерегистрируем watch-элементы после замены
				this._updateWatchElementsAfterRerender(id, newContent);

				console.log(`[XPathWatchProcessor] Successfully rerendered watch element for xpath: ${xpath}`);
			} else {
				console.warn(`[XPathWatchProcessor] Could not find corresponding element for xpath: ${xpath}`);
			}
		} catch (error) {
			console.error(`[XPathWatchProcessor] Error rerendering element for xpath ${xpath}:`, error);
		}
	}

	/**
	 * Находит соответствующий элемент в новом рендере
	 * @param {HTMLElement} tempContainer - Временный контейнер с новым рендером
	 * @param {HTMLElement} originalElement - Оригинальный элемент
	 * @param {HTMLElement} componentContainer - Контейнер компонента
	 * @returns {HTMLElement|null} - Соответствующий элемент или null
	 */
	_findCorrespondingElement(tempContainer, originalElement, componentContainer) {
		// Получаем все возможные идентификаторы элемента
		const exactXpath = originalElement.getAttribute('data-xpath');
		const watchXpath = originalElement.getAttribute('data-xpath-watch');
		
		// 1. Сначала попытаемся найти по точному data-xpath, если он есть
		if (exactXpath) {
			const exactMatches = tempContainer.querySelectorAll(`[data-xpath="${exactXpath}"]`);
			if (exactMatches.length === 1) {
				// Если элемент уникален по data-xpath, возвращаем его
				return exactMatches[0];
			} else if (exactMatches.length > 1) {
				// Если элементов несколько, ищем по DOM-селектору
				const matchBySelector = DomUtils.findByDomSelector(originalElement, exactMatches);
				if (matchBySelector) {
					return matchBySelector;
				}
			}
		}

		// 2. Если нет точного xpath или не найден, ищем по data-xpath-watch
		if (watchXpath) {
			const watchMatches = tempContainer.querySelectorAll(`[data-xpath-watch="${watchXpath}"]`);
			
			if (watchMatches.length === 1) {
				// Если элемент уникален по data-xpath-watch, возвращаем его
				return watchMatches[0];
			} else if (watchMatches.length > 1) {
				// Если элементов несколько, ищем по DOM-селектору
				const matchBySelector = DomUtils.findByDomSelector(originalElement, watchMatches);
				if (matchBySelector) {
					return matchBySelector;
				}
			}
		}

		// 3. Если у элемента есть уникальные атрибуты (id), пробуем их
		const uniqueIdentifier = DomUtils.getUniqueIdentifier(originalElement);
		if (uniqueIdentifier) {
			const uniqueMatch = tempContainer.querySelector(uniqueIdentifier);
			if (uniqueMatch) {
				return uniqueMatch;
			}
		}

		// 4. В крайнем случае пробуем относительный путь
		const relativePath = DomUtils.getRelativePath(originalElement, componentContainer);
		if (relativePath) {
			const relativeMatch = tempContainer.querySelector(relativePath);
			if (relativeMatch) {
				return relativeMatch;
			}
		}

		console.warn(`[XPathWatchProcessor] Could not find corresponding element for xpath: ${watchXpath || exactXpath || 'unknown'}`);
		return null;
	}

	/**
	 * Перерегистрирует watch-элементы после перерисовки
	 * @param {string} id - ID компонента
	 * @param {HTMLElement} newElement - Новый элемент
	 */
	_updateWatchElementsAfterRerender(id, newElement) {
		const component = this.vroc.components.get(id);
		if (!component) return;

		// Находим все watch-элементы в новом элементе
		newElement.querySelectorAll('[data-xpath-watch]').forEach(el => {
			const xpath = el.getAttribute('data-xpath-watch');
			this.registerWatchElement(el, xpath);
		});
	}

	/**
	 * Создает регулярное выражение для XPath паттерна
	 * @param {string} xpath - XPath паттерн
	 * @returns {RegExp} - Регулярное выражение
	 */
	_createXPathPattern(xpath) {
		// Преобразуем XPath в регулярное выражение для сопоставления
		// Например: /module/travelers -> проверяем, начинается ли patch.xpath с этого паттерна
		const escaped = xpath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		return new RegExp(`^${escaped}`);
	}

	/**
	 * Проверяет, соответствует ли patch.xpath watch-паттерну
	 * @param {string} patchXpath - XPath из патча
	 * @param {string} watchXpath - Watch XPath паттерн
	 * @param {RegExp} pattern - Скомпилированный паттерн
	 * @returns {boolean} - Соответствует ли
	 */
	_matchesXPathPattern(patchXpath, watchXpath, pattern) {
		// Проверяем точное совпадение или если patch затрагивает дочерние элементы
		return patchXpath === watchXpath ||
			   patchXpath.startsWith(watchXpath + '/') ||
			   pattern.test(patchXpath);
	}

	/**
	 * Очищает все watch-элементы для компонента
	 * @param {string} id - ID компонента
	 */
	clearWatchElements(id) {
		const component = this.vroc.components.get(id);
		if (!component) return;

		// Находим все watch-элементы компонента и удаляем их
		for (const [xpath, elements] of this.watchElements) {
			for (const element of elements) {
				if (component.container.contains(element)) {
					elements.delete(element);
				}
			}

			if (elements.size === 0) {
				this.watchElements.delete(xpath);
				this.watchPatterns.delete(xpath);
			}
		}
	}

	/**
	 * Получает статистику по watch-элементам
	 * @returns {Object} - Статистика
	 */
	getStats() {
		return {
			totalPatterns: this.watchElements.size,
			totalElements: Array.from(this.watchElements.values())
				.reduce((sum, set) => sum + set.size, 0),
			patterns: Array.from(this.watchElements.keys())
		};
	}
}
